import { useIsAuthenticated, useSessionWasRefreshed } from '@zageno/auth';
import { CartModal, SelfServiceModal } from '@zageno/checkout';
import { getEnvironment, isProduction } from '@zageno/config';
import logger from '@zageno/datadog';
import { setUser, useCurrentUser } from '@zageno/profile';
import {
  AppBar,
  Box,
  Button,
  Container,
  HijackBanner,
  LoginButton,
  Page,
  Ribbon,
  Toolbar,
  ZagenoLinkLogo,
} from '@zageno/ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchBarWidget } from '@zageno/catalog';
import DesktopNavigation from './components/DesktopNavigation';
import HelpMenu from './components/HelpMenu';
import MobileNavigation from './components/MobileNavigation';
import UserMenu from './components/UserMenu';
import { getPages } from './features/header/pagesSlice';
import {
  selectHasSelfServiceEnabled,
  selectUsername,
  setHasApprovalFlowEnabled,
  setHasSelfServiceEnabled,
  setHasTableauEnabled,
  setIsManageOrg,
  setUsername,
} from './features/userSlice';
import { useAppDispatch, useAppSelector, useCurrentPath, useIsCurrentPath } from './hooks';
import store from './store';

const homepageUrl = `${window.location.protocol}//${window.location.host}`;

export default function App() {
  const dispatch = useAppDispatch();
  const [isSearchbarOpen, setIsSearchbarOpen] = useState(false);
  const currentPath = useCurrentPath();

  // use React hook to check whether user is authenticated or not
  const isAuthenticated = useIsAuthenticated();
  const user = useCurrentUser();

  // use React hook to check whether the current okta session was refreshed and refresh pygeno session
  const { refreshed, accessToken, idToken } = useSessionWasRefreshed();

  // following code read data from redux
  const userName = useAppSelector(selectUsername);
  const hasSelfServiceEnabled = useAppSelector(selectHasSelfServiceEnabled);
  const isRoot = useIsCurrentPath('/');

  const openSearchbar = () => {
    setIsSearchbarOpen(true);
  };

  const closeSearchbar = () => {
    setIsSearchbarOpen(false);
  };

  const { t } = useTranslation();

  const fetchData = useCallback(async () => {
    if (!isAuthenticated || !user) {
      // When user isn't authenticated, forget username
      dispatch(setUsername(null));
    } else {
      dispatch(setUsername(`${String(user.first_name)} ${String(user.last_name)}`));
      dispatch(setIsManageOrg(user.can_manage_organization || false));
      dispatch(setHasApprovalFlowEnabled(user.has_approval_flow_enabled || false));
      dispatch(setHasSelfServiceEnabled(user.has_self_service_enabled || false));
      dispatch(setHasTableauEnabled(user.has_tableau_enabled || false));
    }

    await store.dispatch(getPages({ isAuthenticated }));
  }, [isAuthenticated, user, dispatch]);

  useEffect(() => {
    fetchData().catch(err => logger.error(err));
  }, [dispatch, fetchData]);

  useEffect(() => {
    if (isAuthenticated && refreshed) {
      setUser(accessToken, idToken)
        .then(() => fetchData())
        .catch(err => logger.error(err));
    }
  }, [refreshed, accessToken, idToken, isAuthenticated, fetchData]);

  return (
    <Page>
      {!isProduction() && <Ribbon label={getEnvironment()} />}

      <AppBar position="relative" color="transparent">
        {user?.is_hijacked && <HijackBanner userName={userName} />}

        <Container maxWidth="xl" sx={user?.is_hijacked ? { mt: 5 } : {}}>
          <Toolbar disableGutters>
            <ZagenoLinkLogo url={homepageUrl} logoSx={{ verticalAlign: 'middle' }} />

            {!isSearchbarOpen && (
              <Box
                sx={theme => ({
                  flexGrow: 0,
                  display: { xs: 'none', md: 'flex' },
                  ml: 1,
                  [theme.breakpoints.down('lg')]: {
                    ' > div:not(.MuiMenu-root)': {
                      display: 'none',
                    },
                    '> div:not(.MuiMenu-root):nth-child(-n+2)': {
                      display: 'block',
                    },
                  },
                })}
                data-testid="desktop-navigation"
              >
                <DesktopNavigation />
              </Box>
            )}

            {isAuthenticated && !isRoot && (
              <Box
                sx={{ ml: 3, mr: 2, flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'end' }}
                data-testid="desktop-search"
              >
                <SearchBarWidget onFocus={openSearchbar} onFocusOut={closeSearchbar} />
              </Box>
            )}

            {isAuthenticated !== null && (
              <Box sx={{ flexGrow: 0, marginLeft: 'auto' }}>
                {isAuthenticated ? (
                  <>
                    {hasSelfServiceEnabled && <SelfServiceModal />}
                    <CartModal />

                    {userName && <UserMenu logoutUrl="/accounts/logout" />}
                    <HelpMenu />
                  </>
                ) : (
                  <Box sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                    <LoginButton link={`/accounts/login?referer=${currentPath}`} />

                    <Button variant="ghost" href="/contact-us/" sx={{ ml: 1 }}>
                      {t('Contact us')}
                    </Button>
                  </Box>
                )}

                <MobileNavigation isAuthenticated={isAuthenticated} />
              </Box>
            )}
          </Toolbar>
        </Container>
        {isAuthenticated && !isRoot && (
          <Container
            data-testid="search-bar-mobile-container"
            maxWidth="xl"
            sx={{
              background: 'linear-gradient(90deg, rgba(247,253,243,1) 0%, rgba(239,249,254,1) 100%)',
              backgroundPosition: '0 60px',
              minHeight: '76px',
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Toolbar disableGutters>
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'end' }} data-testid="mobile-search">
                <SearchBarWidget />
              </Box>
            </Toolbar>
          </Container>
        )}
      </AppBar>
    </Page>
  );
}
